'use client';

import { useMemo } from 'react';
import { useUser } from '@/contexts/user-context';
import { useAnalytics } from '@/hooks/use-analytics';
import { getAttributionContext } from '@/lib/attribution';
import { authClient } from '@/lib/auth-client';

type BasePayload = Record<string, string | number | boolean | null | undefined>;
type ServerConversionConfig = {
  name: 'CompleteRegistration' | 'StartTrial';
  value?: number;
  currency?: string;
  eventId?: string;
};

const RANDOM_MAX = 1_000_000;

function createEventId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  const now = Date.now();
  const rand = Math.floor(Math.random() * RANDOM_MAX);
  return `${now}-${rand}`;
}

function withContext(
  defaults: BasePayload,
  extra?: Record<string, unknown>
): Record<string, unknown> {
  return { ...defaults, ...(extra || {}) };
}

export function useTrackingEvents() {
  const { data: session } = authClient.useSession();
  const { userProgress } = useUser();
  const analytics = useAnalytics();

  const context = useMemo(() => {
    const attribution = getAttributionContext();
    return {
      user_id: session?.user?.id ?? null,
      role: (session?.user as { role?: string } | undefined)?.role ?? null,
      plan: userProgress?.subscriptionStatus ?? null,
      paywall_variant: userProgress?.paywallVariant ?? null,
      ...attribution,
    };
  }, [session?.user, userProgress?.subscriptionStatus, userProgress?.paywallVariant]);

  const trackEvent = (
    event: string,
    extra?: Record<string, unknown>,
    serverConversion?: ServerConversionConfig
  ) => {
    const eventId = serverConversion?.eventId ?? createEventId();
    const payload = withContext(context, { event_id: eventId, ...(extra || {}) });
    analytics.track(event, payload);
    if (serverConversion === undefined) {
      return eventId;
    }
    analytics.sendServerConversion(serverConversion.name, {
      value: serverConversion.value,
      currency: serverConversion.currency,
      eventId,
    });
    return eventId;
  };

  return {
    trackSignupSuccess: (extra?: Record<string, unknown>, server?: ServerConversionConfig) =>
      trackEvent('signup_success', extra, server),
    trackAccountCreated: (extra?: Record<string, unknown>, server?: ServerConversionConfig) =>
      trackEvent('account_created', extra, server),
    trackLandingView: (extra?: Record<string, unknown>) => trackEvent('landing_view', extra),
    trackOnboardingStep: (extra?: Record<string, unknown>) => trackEvent('onboarding_step', extra),
    trackOnboardingStepFailed: (extra?: Record<string, unknown>) =>
      trackEvent('onboarding_step_failed', extra),
    trackOnboardingStarted: (extra?: Record<string, unknown>) =>
      trackEvent('onboarding_started', extra),
    trackOnboardingResumed: (extra?: Record<string, unknown>) =>
      trackEvent('onboarding_resumed', extra),
    trackFirstInsightViewed: (extra?: Record<string, unknown>) =>
      trackEvent('first_insight_viewed', extra),
    trackBirthDataCompleted: (extra?: Record<string, unknown>) =>
      trackEvent('birth_data_completed', extra),
    trackInsightSaved: (extra?: Record<string, unknown>) => trackEvent('insight_saved', extra),
    trackActivationActionSelected: (extra?: Record<string, unknown>) =>
      trackEvent('activation_action_selected', extra),
    trackTrialGoalStarted: (extra?: Record<string, unknown>) =>
      trackEvent('trial_goal_started', extra),
    trackDashboardActionViewed: (extra?: Record<string, unknown>) =>
      trackEvent('dashboard_action_viewed', extra),
    trackDashboardActionSelected: (extra?: Record<string, unknown>) =>
      trackEvent('dashboard_action_selected', extra),
    trackPricingPlanViewed: (extra?: Record<string, unknown>) =>
      trackEvent('pricing_plan_viewed', extra),
    trackPricingPlanSelected: (extra?: Record<string, unknown>) =>
      trackEvent('pricing_plan_selected', extra),
    trackTrialCardViewed: (extra?: Record<string, unknown>) =>
      trackEvent('trial_card_viewed', extra),
    trackCancellationReasonSubmitted: (extra?: Record<string, unknown>) =>
      trackEvent('cancellation_reason_submitted', extra),
    trackSplashScreenView: (extra?: Record<string, unknown>) =>
      trackEvent('splash_screen_view', extra),
    trackSplashScreenSkip: (extra?: Record<string, unknown>) =>
      trackEvent('splash_screen_skip', extra),
    trackPaywallView: (extra?: Record<string, unknown>) => trackEvent('paywall_view', extra),
    trackTrialStarted: (extra?: Record<string, unknown>, server?: ServerConversionConfig) =>
      trackEvent('trial_started', extra, server),
    trackTrialActivated: (extra?: Record<string, unknown>) => trackEvent('trial_activated', extra),
    trackSubscriptionStarted: (extra?: Record<string, unknown>, server?: ServerConversionConfig) =>
      trackEvent('subscription_started', extra, server),
    trackSubscriptionCancelled: (extra?: Record<string, unknown>) =>
      trackEvent('subscription_cancelled', extra),
    trackPaywallBlock: (extra?: Record<string, unknown>) => trackEvent('paywall_block', extra),
    trackPaywallPlanSelect: (extra?: Record<string, unknown>) =>
      trackEvent('paywall_plan_select', extra),
    trackPaywallBillingToggle: (extra?: Record<string, unknown>) =>
      trackEvent('paywall_billing_toggle', extra),
    trackPaywallCompareToggle: (extra?: Record<string, unknown>) =>
      trackEvent('paywall_compare_toggle', extra),
    trackUpgradeClick: (extra?: Record<string, unknown>) => trackEvent('upgrade_click', extra),
    trackCheckoutStart: (extra?: Record<string, unknown>) => trackEvent('checkout_start', extra),
    trackCheckoutSubmit: (extra?: Record<string, unknown>) => trackEvent('checkout_submit', extra),
    trackCheckoutSuccess: (extra?: Record<string, unknown>, server?: ServerConversionConfig) =>
      trackEvent('checkout_success', extra, server),
    trackCheckoutFailed: (extra?: Record<string, unknown>) => trackEvent('checkout_failed', extra),
    trackCheckoutRedirect: (extra?: Record<string, unknown>) =>
      trackEvent('checkout_redirect', extra),
    trackBillingModalOpen: (extra?: Record<string, unknown>) =>
      trackEvent('billing_modal_open', extra),
    trackBillingUpgradeClick: (extra?: Record<string, unknown>) =>
      trackEvent('billing_modal_upgrade_click', extra),
    trackBillingCancelClick: (extra?: Record<string, unknown>) =>
      trackEvent('billing_modal_cancel_click', extra),
    trackBillingReactivateClick: (extra?: Record<string, unknown>) =>
      trackEvent('billing_modal_reactivate_click', extra),
    trackBillingPaymentMethodClick: (extra?: Record<string, unknown>) =>
      trackEvent('billing_modal_payment_method_click', extra),
    trackLimitReached: (extra?: Record<string, unknown>) => trackEvent('limit_reached', extra),
    trackOfferTrialStart: (extra?: Record<string, unknown>) =>
      trackEvent('offer_trial_start', extra),
    trackOfferSkip: (extra?: Record<string, unknown>) => trackEvent('offer_trial_skip', extra),
  };
}
